import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChartType } from 'chart.js';
import 'chartjs-adapter-date-fns';
import ChartLib from 'chart.js/auto';
import { ReportDisplayStyle } from '../../api/types/generated/reports_get.response';
import { useUserState } from '../../hooks/context/useUserState';
import './Chart.css';
import { ReportDataResponseBody } from '../../api/types/generated/report_data_post.response';
import { LANGUAGES } from '../../constants';
import { getGraphColors } from '../../utils/filters/getGraphColors';
import SeptimLoadingSpinner from '../Manager/SeptimLoadingSpinner/SeptimLoadingSpinner';
import { getLegendFontConfig } from './utils/getLegendFontConfig';

interface ChartProps {
  data: ReportDataResponseBody;
  displayStyle: ReportDisplayStyle[number];
  windowWidth: number;
}

export default function Chart({ data, displayStyle, windowWidth }: ChartProps) {
  const {
    state: { language },
  } = useUserState();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<any>(null);
  const chartOptions: any = useMemo(
    () => ({
      ...displayStyle.config,
      aspectRatio: displayStyle.aspectRatio,
    }),
    [displayStyle]
  );
  const [legend, setLegend] = useState<any>(null);
  const pieChartTypes = ['pie', 'doughnut'];

  const htmlLegendPlugin = {
    id: 'htmlLegend',
    afterUpdate(chart) {
      // Reuse the built-in legendItems generator
      const items = chart.options.plugins.legend.labels.generateLabels(chart);
      const legendItems = items.map((item) => ({
        onClick: () => {
          const { type } = chart.config;
          if (pieChartTypes.includes(type)) {
            // Pie and doughnut charts only have a single dataset and visibility is per item
            chart.toggleDataVisibility(item.index);
          } else {
            chart.setDatasetVisibility(
              item.datasetIndex,
              !chart.isDatasetVisible(item.datasetIndex)
            );
          }
          chart.update();
        },
        color: item.fillStyle,
        hidden: item.hidden,
        text: item.text,
      }));
      setLegend(legendItems);
    },
  };
  useEffect(() => {
    if (chartOptions) {
      chartOptions.locale = language;
      if (chartOptions.scales) {
        const selectedLanguage = LANGUAGES.find(({ id }) => id === language);
        const dateAdapter = {
          date: {
            locale: selectedLanguage?.dateLocale,
          },
        };
        if (chartOptions.scales.x && chartOptions.scales.x.type === 'time') {
          chartOptions.scales.x.adapters = dateAdapter;
        }
        if (chartOptions.scales.y && chartOptions.scales.y.type === 'time') {
          chartOptions.scales.y.adapters = dateAdapter;
        }
      }
    }
    const context = canvasRef.current?.getContext('2d');
    const type = displayStyle.type as ChartType;
    const possibleChartTypes = [
      'bar',
      'line',
      'scatter',
      'bubble',
      'pie',
      'doughnut',
      'polarArea',
      'radar',
    ];
    if (
      context &&
      data &&
      chartOptions &&
      type &&
      possibleChartTypes.includes(type)
    ) {
      const individualItemsChartTypes = ['pie', 'doughnut', 'bar'];
      const chartData: any = {
        ...data,
        datasets: data.datasets.map((dataset, index) => {
          let chartData;
          if (individualItemsChartTypes.includes(type)) {
            chartData = dataset.reportData.map((item) => item[1]);
          } else {
            chartData = [...dataset.reportData];
          }
          const newDataset = {
            ...dataset,
            data: chartData,
          } as any;
          if (pieChartTypes.includes(type)) {
            delete newDataset.label;
          }
          delete newDataset.reportData;
          // Line and bar chart needs to have different background and border colors for every dataset, not item
          if (type === 'line' || type === 'bar') {
            const colors = getGraphColors(data.datasets.length);
            newDataset.backgroundColor = colors[index];
            newDataset.borderColor = colors[index];
          } else {
            const colors = getGraphColors(newDataset.data.length);
            newDataset.backgroundColor = colors;
            // Set borders to the same colors as elements to fix white outline glitch on hover
            if (pieChartTypes.includes(type)) {
              chartOptions.borderColor = colors;
            }
          }
          return newDataset;
        }),
      };

      const formattingStringsAvailable = chartData.datasets.find(
        (dataset) => dataset.formattingStrings
      );
      if (!chartOptions.plugins) {
        chartOptions.plugins = {
          tooltip: {},
          legend: {},
        };
      }
      if (!chartOptions.plugins.legend) {
        chartOptions.plugins.legend = {};
      }
      chartOptions.plugins.legend = {
        ...chartOptions.plugins.legend,
        display: false,
      };
      // Use formattingFunction only if at least one dataset contains formattingStrings
      if (formattingStringsAvailable) {
        const formattingFunction = (context) => {
          if (context.dataset.formattingStrings) {
            const result = Array.from(
              { length: context.dataset.data.length },
              (value, index) =>
                context.dataset.formattingStrings[
                  index % context.dataset.formattingStrings.length
                ]
            );
            const formattingString = result[context.dataIndex];
            return formattingString.replace('{value}', context.formattedValue);
          }
          return context.formattedValue;
        };
        chartOptions.plugins.tooltip = {
          ...chartOptions.plugins.tooltip,
          callbacks: {
            label: formattingFunction,
          },
        };
      }
      if (individualItemsChartTypes.includes(type) && data.datasets[0]) {
        chartData.labels = data.datasets[0].reportData.map((item) => item[0]);
      }
      if (chartRef.current) {
        chartRef.current.destroy();
      }
      chartRef.current = new ChartLib(context, {
        type,
        data: chartData,
        options: chartOptions,
        plugins: [htmlLegendPlugin],
      });
    }
  }, [data, chartOptions, language, windowWidth]);

  const legendFontConfig = getLegendFontConfig(displayStyle.config);
  const legendStyle: any = {};
  if (legendFontConfig?.size) {
    legendStyle.fontSize = `${legendFontConfig.size}px`;
  }
  if (legendFontConfig?.family) {
    legendStyle.fontFamily = legendFontConfig.family;
  }

  return (
    <div className="chart-container">
      {!displayStyle.type && <SeptimLoadingSpinner />}
      <canvas ref={canvasRef} />
      {legend && (
        <ul className="chart-legend" style={legendStyle}>
          {legend.map(({ text, onClick, hidden, color }, index) => (
            <li
              {...(hidden && { className: 'hidden' })}
              onClick={onClick}
              key={index}
            >
              <div style={{ backgroundColor: color }}></div>
              <p>{text}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
